import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  FormControl,
  FormLabel,
  Input,
  IconButton,
} from "@chakra-ui/react";
import { MdAdd, MdClose, MdCardGiftcard, MdRemove } from "react-icons/md";
import Card from "components/card/Card";
import { Pagination } from "antd";

export default function ServiceRules({
  allServices, // Array of available services from API
  totalServices, // Total count for pagination
  currentPage, // Current page number
  onPageChange, // Handler for page change
  selectedServices, // Array of selected service objects
  setSelectedServices, // Setter for selected services
  globalServicePoints, // Global reward points value
  setGlobalServicePoints, // Setter for global points
  loading, // Loading state for API fetch
  onSave, // Save handler (if needed in context)
  isSaveDisabled, // Whether save button in parent should be disabled
}) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const secondaryText = useColorModeValue("secondaryGray.600", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const modalHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const accentColor = "#1677ff";
  const accentAlpha = "rgba(22, 119, 255, 0.1)";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelectedIds, setTempSelectedIds] = useState(new Set());

  // Reset temp selection when modal opens
  useEffect(() => {
    if (isModalOpen) {
      const ids = new Set(selectedServices.map((s) => s.id));
      setTempSelectedIds(ids);
    }
  }, [isModalOpen, selectedServices]);

  const handleToggleService = (service) => {
    const newIds = new Set(tempSelectedIds);
    if (newIds.has(service.id)) {
      newIds.delete(service.id);
    } else {
      newIds.add(service.id);
    }
    setTempSelectedIds(newIds);
  };

  const handleConfirmSelection = () => {
    // Keep already selected services that are still in temp
    const keptServices = selectedServices.filter((s) =>
      tempSelectedIds.has(s.id)
    );

    // Add new ones from allServices
    const newServicesFromCurrentPage = allServices.filter(
      (s) =>
        tempSelectedIds.has(s.id) &&
        !keptServices.find((ext) => ext.id === s.id)
    );

    setSelectedServices([...keptServices, ...newServicesFromCurrentPage]);
    setIsModalOpen(false);
  };

  const handleRemoveService = (serviceId) => {
    setSelectedServices(selectedServices.filter((s) => s.id !== serviceId));
  };

  const handleIncrement = () => {
    setGlobalServicePoints(parseInt(globalServicePoints) + 1);
  };

  const handleDecrement = () => {
    if (globalServicePoints > 0) {
      setGlobalServicePoints(parseInt(globalServicePoints) - 1);
    }
  };

  const handleChangePoints = (e) => {
    const val = e.target.value;
    if (val === "") {
      setGlobalServicePoints("");
      return;
    }
    // Prevent leading zeros unless it's just "0"
    if (val.length > 1 && val.startsWith("0")) {
      const sanitized = val.replace(/^0+/, "");
      setGlobalServicePoints(sanitized || "0");
      return;
    }
    const parsed = parseInt(val, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      setGlobalServicePoints(val);
    }
  };

  const handleBlurPoints = () => {
    if (globalServicePoints === "") {
      setGlobalServicePoints(0);
    } else {
      setGlobalServicePoints(parseInt(globalServicePoints, 10));
    }
  };

  return (
    <>
      <Card mb="20px">
        <Flex align="center" justify="space-between" mb="20px">
          <Text color={accentColor} fontSize="22px" fontWeight="700">
            Service Rules
          </Text>
          <Button
            bg={accentColor}
            color="white"
            _hover={{ bg: "#0870ad" }}
            _active={{ bg: "#076094" }}
            fontSize="sm"
            fontWeight="500"
            leftIcon={<Icon as={MdAdd} color="white" />}
            onClick={() => setIsModalOpen(true)}
          >
            Add Rule
          </Button>
        </Flex>

        {/* Selected Services List (Compact) */}
        <Box mb="24px">
          {selectedServices.length > 0 ? (
            <Stack spacing="8px">
              {selectedServices.map((service) => (
                <Flex
                  key={service.id}
                  p="10px 16px"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="8px"
                  justify="space-between"
                  align="center"
                  bg="transparent"
                >
                  <Box>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {service.name || service.service_name}
                    </Text>
                    <Text color={secondaryText} fontSize="xs">
                      ID: {service.id}
                    </Text>
                  </Box>
                  <IconButton
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    icon={<Icon as={MdClose} />}
                    onClick={() => handleRemoveService(service.id)}
                    aria-label="Remove service"
                  />
                </Flex>
              ))}
            </Stack>
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="20px"
              textAlign="center"
              bg="gray.50"
              borderRadius="8px"
              border="1px dashed"
              borderColor="gray.300"
            >
              <Icon
                as={MdCardGiftcard}
                width="30px"
                height="30px"
                color="gray.400"
                mb="8px"
              />
              <Text color="gray.500" fontSize="sm">
                No services selected
              </Text>
            </Flex>
          )}
        </Box>

        {/* Global Reward Points Input Section */}
        <Box pt="20px" borderTop="1px solid" borderColor={borderColor}>
          <FormControl>
            <FormLabel color={textColor} fontSize="sm" fontWeight="500">
              Global Reward Points (Points per visit)
            </FormLabel>
            <Flex align="center" gap="12px" mb="20px">
              <IconButton
                icon={<MdRemove />}
                onClick={handleDecrement}
                aria-label="Decrease points"
                size="sm"
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
              />
              <Input
                type="number"
                value={globalServicePoints}
                onChange={handleChangePoints}
                onBlur={handleBlurPoints}
                width="100px"
                textAlign="center"
                fontWeight="700"
                size="md"
                borderColor={borderColor}
                _focus={{ borderColor: accentColor }}
              />
              <IconButton
                icon={<MdAdd />}
                onClick={handleIncrement}
                aria-label="Increase points"
                size="sm"
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
              />
            </Flex>
          </FormControl>
        </Box>

        {/* Selection Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent maxH="80vh">
            <ModalHeader color={accentColor}>Select Services</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" maxH="60vh">
              {loading ? (
                <Flex justify="center" py={10}>
                  <Spinner color={accentColor} />
                </Flex>
              ) : (
                <Stack spacing={2}>
                  {allServices.map((service) => {
                    const isSelected = tempSelectedIds.has(service.id);
                    return (
                      <Flex
                        key={service.id}
                        p="12px"
                        borderWidth="1px"
                        borderColor={isSelected ? accentColor : borderColor}
                        borderRadius="md"
                        align="center"
                        justify="space-between"
                        bg={isSelected ? accentAlpha : "transparent"}
                        _hover={{ bg: isSelected ? accentAlpha : modalHoverBg }}
                        cursor="pointer"
                        onClick={() => handleToggleService(service)}
                        transition="all 0.2s"
                      >
                        <Box>
                          <Text
                            fontWeight={isSelected ? "700" : "500"}
                            color={isSelected ? accentColor : textColor}
                          >
                            {service.name || service.service_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {service.id}
                          </Text>
                        </Box>
                        {isSelected && (
                          <Icon as={MdCardGiftcard} color={accentColor} />
                        )}
                      </Flex>
                    );
                  })}
                </Stack>
              )}

              <Flex justify="center" mt={6} pb={2}>
                <Pagination
                  current={currentPage}
                  total={totalServices}
                  pageSize={10}
                  onChange={onPageChange}
                  size="small"
                  showSizeChanger={false}
                />
              </Flex>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
                onClick={handleConfirmSelection}
              >
                Confirm Selection
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Card>
    </>
  );
}
